import type { ReactNode } from "react"
import { memo, useEffect, useMemo } from "react"
import { useQuery } from "@rocicorp/zero/react"
import { useLocation, useParams } from "@tanstack/react-router"
import {
	IconCheckFill18 as Check,
	IconChevronRightFill18 as ChevronRight,
	IconCircleDottedUserFill18 as NoAssignee,
} from "nucleo-ui-fill-18"

import type { BrandfetchData } from "@productlane/api/src/browser"
import type { PriorityType } from "@productlane/ui"
import { Avatar, PriorityIcons, PriorityNaming } from "@productlane/ui"
import {
	CommandGroup,
	CommandItem,
} from "@productlane/ui/src/components/internal/command"
import { mutators } from "@productlane/zero/mutators"
import { queries, threadDetailQueryArgs } from "@productlane/zero/queries"

import { analytics } from "@/lib/analytics"
import {
	assigneeOptions,
	IMPORTANCE_OPTIONS,
	optionDigit,
	optionForDigit,
	shouldPickByDigit,
} from "@/lib/cmdk-thread-options"
import { getProjectIcon } from "@/lib/projects"
import { useSession } from "@/lib/session"
import { useCmdkState } from "@/lib/states/cmdk-state"
import { mutateOk, useZero } from "@/lib/zero"
import { CompanyLogo } from "@/screens/app/customers/company-logo"
import { toast } from "./toast"

// Same args as the thread page, so the palette reads the row it already synced.
export function useCmdkThread() {
	const { pathname } = useLocation()
	const params = useParams({ strict: false })
	const threadId =
		pathname.startsWith("/threads/") && params.id ? params.id : ""
	const [thread] = useQuery(
		queries.threadWithFullDetails(threadDetailQueryArgs(threadId)),
		{ ttl: "none" },
	)
	return threadId ? thread : undefined
}

type CmdkThread = NonNullable<ReturnType<typeof useCmdkThread>>

// The sidebar and the tag picker run these exact queries, so they are already local.
const TAG_QUERY_ARGS = { query: "", limit: 100 }

function useThreadUpdates(thread: CmdkThread | undefined) {
	const zero = useZero()
	const { setIsOpen } = useCmdkState()

	const update = async (
		patch: {
			assigneeId?: string | null
			painLevel?: PriorityType
			contactId?: string | null
			companyId?: string | null
		},
		title: string,
		event: string,
	) => {
		if (!thread) return
		setIsOpen(false)
		await mutateOk(
			zero.mutate(mutators.threads.update({ id: thread.id, ...patch })),
		)
		toast.success({ title })
		analytics.track(event, { cmdk: true, threadId: thread.id })
	}

	return {
		assign: (userId: string | null, name: string | null | undefined) =>
			update(
				{ assigneeId: userId },
				userId ? `Assigned to ${name ?? "teammate"}` : "Unassigned",
				"assign-thread",
			),
		setImportance: (level: PriorityType) =>
			update(
				{ painLevel: level },
				level === "UNKNOWN"
					? "Importance cleared"
					: `Importance set to ${PriorityNaming[level].toLowerCase()}`,
				"set-thread-importance",
			),
		setContact: (contact: {
			id: string
			name: string | null
			email: string
			companyId?: string | null
		}) =>
			update(
				{ contactId: contact.id, companyId: contact.companyId ?? null },
				`Contact set to ${contact.name || contact.email}`,
				"set-thread-contact",
			),
		setCompany: (company: { id: string; name: string }) =>
			update(
				{ companyId: company.id },
				`Company set to ${company.name}`,
				"set-thread-company",
			),
		toggleTag: async (tag: { id: string; name: string }) => {
			if (!thread) return
			const linked = thread.tags.some((t) => t.id === tag.id)
			setIsOpen(false)
			if (linked) {
				// Highlights of a removed tag go with it, as in the sidebar.
				document
					.querySelectorAll(`mark[data-id="${tag.id}"]`)
					.forEach((mark) => mark.replaceWith(mark.innerHTML))
			}
			await mutateOk(
				zero.mutate(
					mutators.tags.linkTag({ threadId: thread.id, tagId: tag.id }),
				),
			)
			toast.success({
				title: linked ? `Removed tag ${tag.name}` : `Added tag ${tag.name}`,
			})
			analytics.track("toggle-thread-tag", {
				cmdk: true,
				threadId: thread.id,
				tagId: tag.id,
				linked: !linked,
			})
		},
	}
}

function useDigitPick<T>(
	options: ReadonlyArray<T>,
	onPick: (option: T) => void,
) {
	const { commandText } = useCmdkState()
	useEffect(() => {
		const onKeyDown = (e: KeyboardEvent) => {
			if (!shouldPickByDigit(e, commandText)) return
			const option = optionForDigit(options, e.key)
			if (option === undefined) return
			e.preventDefault()
			e.stopPropagation()
			onPick(option)
		}
		window.addEventListener("keydown", onKeyDown, { capture: true })
		return () =>
			window.removeEventListener("keydown", onKeyDown, { capture: true })
	})
}

function useAssigneeOptions() {
	const { session } = useSession()
	const [teamMembers] = useQuery(queries.teamMembers())
	return useMemo(
		() => assigneeOptions(teamMembers, session?.userId),
		[teamMembers, session?.userId],
	)
}

const Selected = ({ when }: { when: boolean }) =>
	when ? <Check className="h-4 w-4 flex-none text-primary" /> : null

const MemberAvatar = (props: { name: string; image?: string | null }) => (
	<Avatar size="xs" name={props.name} image={props.image} />
)

export const AssigneePage = memo(() => {
	const thread = useCmdkThread()
	const { assign } = useThreadUpdates(thread)
	const options = useAssigneeOptions()

	useDigitPick(options, (o) => void assign(o.userId, o.member?.user?.name))

	if (!thread) return null

	return (
		<CommandGroup>
			{options.map((option, index) => {
				const name = option.member?.user?.name ?? "No assignee"
				return (
					<CommandItem
						size="lg"
						key={option.userId ?? "none"}
						value={`${name} ${option.userId ?? "none"}`}
						Icon={
							option.member
								? () => (
										<MemberAvatar
											name={name}
											image={option.member?.user?.imageUrl}
										/>
									)
								: NoAssignee
						}
						hint={<Selected when={thread.assigneeId === option.userId} />}
						shortcuts={optionDigit(index) ? [optionDigit(index)] : undefined}
						onSelect={() =>
							void assign(option.userId, option.member?.user?.name)
						}
					>
						{name}
					</CommandItem>
				)
			})}
		</CommandGroup>
	)
})

export const ImportancePage = memo(() => {
	const thread = useCmdkThread()
	const { setImportance } = useThreadUpdates(thread)

	useDigitPick(IMPORTANCE_OPTIONS, (level) => void setImportance(level))

	return (
		<CommandGroup>
			{IMPORTANCE_OPTIONS.map((level, index) => {
				const Icon = PriorityIcons[level]
				return (
					<CommandItem
						size="lg"
						key={level}
						value={PriorityNaming[level]}
						Icon={() => <Icon className="h-4 w-4 flex-none" />}
						hint={<Selected when={thread?.painLevel === level} />}
						shortcuts={[optionDigit(index)]}
						onSelect={() => void setImportance(level)}
					>
						{PriorityNaming[level]}
					</CommandItem>
				)
			})}
		</CommandGroup>
	)
})

function useTagOptions() {
	const [tags] = useQuery(queries.searchTags(TAG_QUERY_ARGS))
	const [tagGroups] = useQuery(queries.tagGroups())
	return useMemo(() => {
		const groups = new Map(tagGroups.map((g) => [g.id, g]))
		return tags.map((tag) => ({ tag, group: groups.get(tag.tagGroupId) }))
	}, [tags, tagGroups])
}

const TagGlyph = ({ icon, color }: { icon: string | null; color?: string }) => {
	const TagIcon = getProjectIcon(icon)
	return <TagIcon className="h-4 w-4 flex-none" style={{ color }} />
}

export const TagsPage = memo(() => {
	const thread = useCmdkThread()
	const { toggleTag } = useThreadUpdates(thread)
	const options = useTagOptions()

	if (!thread) return null

	return (
		<CommandGroup>
			{options.map(({ tag, group }) => (
				<CommandItem
					size="lg"
					key={tag.id}
					value={`${tag.name} ${group?.name ?? ""} ${tag.id}`}
					Icon={() => <TagGlyph icon={tag.icon} color={group?.color} />}
					hint={<Selected when={thread.tags.some((t) => t.id === tag.id)} />}
					onSelect={() => void toggleTag(tag)}
				>
					{tag.name}
				</CommandItem>
			))}
		</CommandGroup>
	)
})

export const ContactPage = memo(() => {
	const thread = useCmdkThread()
	const { commandText } = useCmdkState()
	const { setContact } = useThreadUpdates(thread)
	const [contacts] = useQuery(
		queries.searchContacts({ query: commandText, limit: 20 }),
		{ ttl: "none" },
	)

	if (!thread) return null

	return (
		<CommandGroup>
			{contacts.map((contact) => (
				<CommandItem
					size="lg"
					key={contact.id}
					value={`${contact.name ?? ""} ${contact.email} ${contact.id}`}
					Icon={() => (
						<MemberAvatar
							name={contact.name || contact.email}
							image={contact.imageUrl}
						/>
					)}
					hint={<Selected when={thread.contactId === contact.id} />}
					onSelect={() => void setContact(contact)}
				>
					{contact.name ? `${contact.name} (${contact.email})` : contact.email}
				</CommandItem>
			))}
		</CommandGroup>
	)
})

export const CompanyPage = memo(() => {
	const thread = useCmdkThread()
	const { commandText } = useCmdkState()
	const { setCompany } = useThreadUpdates(thread)
	const [companies] = useQuery(
		queries.searchCompanies({ query: commandText, limit: 20 }),
		{ ttl: "none" },
	)

	if (!thread) return null

	return (
		<CommandGroup>
			{companies.map((company) => (
				<CommandItem
					size="lg"
					key={company.id}
					value={`${company.name} ${company.id}`}
					Icon={() => (
						<CompanyLogo
							brandfetchData={
								company.companyMeta?.brandfetchData as unknown as BrandfetchData
							}
							fallback={company.name}
							linearLogoUrl={company.logoUrl || undefined}
							size="xs"
						/>
					)}
					hint={<Selected when={thread.company?.id === company.id} />}
					onSelect={() => void setCompany(company)}
				>
					{company.name}
				</CommandItem>
			))}
		</CommandGroup>
	)
})

const QuickSetLabel = ({
	verb,
	children,
}: {
	verb: string
	children: ReactNode
}) => (
	<span className="inline-flex min-w-0 items-center gap-1.5">
		<span className="flex-none text-tertiary">{verb}</span>
		<ChevronRight className="h-3 w-3 flex-none text-tertiary" />
		<span className="truncate">{children}</span>
	</span>
)

/** Typing "assign to dan" or "important" sets the value straight from the root list, like Linear. */
export const ThreadQuickSetItems = memo(() => {
	const thread = useCmdkThread()
	const { commandText } = useCmdkState()
	const { assign, setImportance, toggleTag } = useThreadUpdates(thread)
	const assignees = useAssigneeOptions()
	const tags = useTagOptions()

	if (!thread || !commandText.trim()) return null

	return (
		<CommandGroup>
			{assignees.map((option) => {
				const name = option.member?.user?.name ?? "No assignee"
				return (
					<CommandItem
						size="lg"
						key={`assign-${option.userId ?? "none"}`}
						value={`Assign to ${name} ${option.userId ?? "none"}`}
						Icon={
							option.member
								? () => (
										<MemberAvatar
											name={name}
											image={option.member?.user?.imageUrl}
										/>
									)
								: NoAssignee
						}
						hint={<Selected when={thread.assigneeId === option.userId} />}
						onSelect={() =>
							void assign(option.userId, option.member?.user?.name)
						}
					>
						<QuickSetLabel verb="Assign to">{name}</QuickSetLabel>
					</CommandItem>
				)
			})}
			{IMPORTANCE_OPTIONS.map((level) => {
				const Icon = PriorityIcons[level]
				return (
					<CommandItem
						size="lg"
						key={`importance-${level}`}
						value={`Set importance to ${PriorityNaming[level]}`}
						Icon={() => <Icon className="h-4 w-4 flex-none" />}
						hint={<Selected when={thread.painLevel === level} />}
						onSelect={() => void setImportance(level)}
					>
						<QuickSetLabel verb="Set importance to">
							{PriorityNaming[level]}
						</QuickSetLabel>
					</CommandItem>
				)
			})}
			{commandText.trim().length >= 2 &&
				tags.map(({ tag, group }) => {
					const linked = thread.tags.some((t) => t.id === tag.id)
					const verb = linked ? "Remove tag" : "Add tag"
					return (
						<CommandItem
							size="lg"
							key={`tag-${tag.id}`}
							value={`${verb} ${tag.name} ${tag.id}`}
							Icon={() => <TagGlyph icon={tag.icon} color={group?.color} />}
							onSelect={() => void toggleTag(tag)}
						>
							<QuickSetLabel verb={verb}>{tag.name}</QuickSetLabel>
						</CommandItem>
					)
				})}
		</CommandGroup>
	)
})
